import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { HttpStatusCode } from "axios";
import { skipToken } from "@reduxjs/toolkit/query";
import Loader from "_shared/_views/loading/loader";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";
import type AHDate from "_shared/dates/date";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router";
import type Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import { useGetProposalComparisonQuery } from "work/proposals/comparison/_api/proposal-comparison-api";
import ProposalComparison, { type ProposalComparisonColumn } from "work/proposals/comparison/proposal-comparison";
import ProposalComparisonLink from "work/proposals/comparison/_views/proposal-comparison-link";
import {
  PROPOSAL_COMPARISON_ABSENT,
  PROPOSAL_COMPARISON_REFUSED,
  UNAVAILABLE_PROPOSAL_TEXT,
} from "work/proposals/comparison/_views/proposal-comparison-page";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import { proposalListDestination } from "work/proposals/_views/proposal-list-destination";
import Proposal from "work/proposals/proposal";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";

// The page header sits over the top of a drawer's paper, so the panel starts below it, and ends above
// the assistant's bar, as the correspondence pane does.
const paperProps = {
  sx: {
    bottom: shellBottomBarHeight(),
    boxSizing: "border-box",
    height: "auto",
    paddingTop: 8,
  },
} as const;

const Panel = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width: 420,
  maxWidth: "100vw",
  [theme.breakpoints.down("sm")]: {
    width: "100vw",
  },
}));

const Heading = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));

const Muted = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

/**
 * The proposals one request produced, one entry per firm, in a drawer over the proposals list
 * (#6460). The list shows a request as one row when it is grouped by request; this is what that
 * row opens.
 *
 * It reads the whole request from the comparison, so it is complete whatever page the row is on.
 * Only the client side may read a request. A firm whose proposal the reader may not read keeps
 * its entry, marked unavailable, as on the comparison page.
 */
export default function ProposalRequestDrawer(
  props: Readonly<{ rfpId: string | undefined; onClose: () => void }>,
) {
  const { rfpId, onClose } = props;
  return (
    <Drawer anchor="right" open={Boolean(rfpId)} onClose={onClose} slotProps={{ paper: paperProps }}>
      <Panel role="region" aria-label="Proposals of this request">
        {rfpId && <ProposalRequest rfpId={rfpId} onClose={onClose} />}
      </Panel>
    </Drawer>
  );
}

function ProposalRequest(props: Readonly<{ rfpId: string; onClose: () => void }>) {
  const { rfpId, onClose } = props;
  const session = useSession();
  const currentUser = session.user;
  const query = useGetProposalComparisonQuery(rfpId ? { rfpId } : skipToken);
  // `currentData`, so a drawer opened on another request never shows the last one's firms.
  const current = query.currentData;
  const comparison = useMemo(() => {
    if (!current || !currentUser) return undefined;
    return ProposalComparison.fromResult({
      found: current.found.flatMap((json) => {
        const proposal = Proposal.fromJSON({ json, currentUser });
        return proposal ? [proposal] : [];
      }),
      unavailable: current.unavailable,
    });
  }, [current, currentUser]);

  const close = (
    <IconButton aria-label="Close" onClick={onClose} edge="end">
      <CloseIcon />
    </IconButton>
  );

  if (query.error && !query.isFetching) {
    const status = "status" in query.error ? query.error.status : undefined;
    let message = "We could not load the proposals of this request.";
    if (status === HttpStatusCode.Forbidden) message = PROPOSAL_COMPARISON_REFUSED;
    if (status === HttpStatusCode.NotFound) message = PROPOSAL_COMPARISON_ABSENT;
    return (
      <>
        <Heading>
          <Typography variant="h6" component="h2">
            Request
          </Typography>
          {close}
        </Heading>
        <Alert severity="error">{message}</Alert>
      </>
    );
  }
  if (!comparison) return <Loader />;

  const columns = comparison.columns;
  const name = firstReadableName(columns);
  return (
    <>
      <Heading>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ overflowWrap: "anywhere" }}>
            {name}
          </Typography>
          <Muted>Sent to {columns.length} {columns.length === 1 ? "firm" : "firms"}</Muted>
        </Box>
        {close}
      </Heading>
      <ProposalComparisonLink rfpId={rfpId} proposalCount={columns.length} />
      <Divider />
      <List disablePadding aria-label="Firms">
        {columns.map((column) => (
          <ListItem key={column.id} disableGutters divider sx={{ alignItems: "flex-start" }}>
            <FirmEntry column={column} session={session} />
          </ListItem>
        ))}
      </List>
    </>
  );
}

function FirmEntry(props: Readonly<{ column: ProposalComparisonColumn; session: Session }>) {
  const { column, session } = props;
  if (column.kind === "unavailable") return <Muted>{UNAVAILABLE_PROPOSAL_TEXT}</Muted>;
  const { proposal } = column;
  const destination = proposal.id
    ? proposalListDestination(proposal, {
      // Each firm's proposal opens in its own section: one can be live while another is archived.
      userId: session.user?.id,
      isRestrictedOnboarding: session.isRestrictedOnboarding,
    })
    : undefined;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, width: "100%" }}>
      {Masked.isMasked(proposal.vendorTeam) ? (
        <Muted>{maskedProposalFieldNotice(MaskedProposalField.Team)}</Muted>
      ) : (
        <VendorTeamLink displayMode="full" proposal={proposal} session={session} />
      )}
      <Typography variant="body2">{proposal.displayStatus}</Typography>
      <ResponseDue date={proposal.responseDueBy} />
      {destination && (
        <div>
          <Button component={RouterLink} to={destination} size="small" variant="outlined">
            Open proposal
          </Button>
        </div>
      )}
    </Box>
  );
}

function ResponseDue(props: Readonly<{ date: AHDate | undefined }>) {
  const { date } = props;
  if (!date) return null;
  return <Muted>Response due {date.format("MM/DD/YYYY")}</Muted>;
}

/** The request's name: its proposals share the name the client sent (#6460). */
function firstReadableName(columns: readonly ProposalComparisonColumn[]): string {
  for (const column of columns) {
    if (column.kind === "proposal" && column.proposal.name?.value) return column.proposal.name.value;
  }
  return "Request";
}
